import React, { useState, useEffect } from 'react';
import { AppState, User, Category, Puzzle, UserProgress, Challenge, AppSettings } from './types';
import { CATEGORIES, COBALT_BLUE } from './constants';
import { IntroVideo, Login, AuthRestrictionModal, InstructionsModal, ExitConfirmModal, SettingsModal, BioModal, SupportModal } from './components/Layouts';
import { Settings, WifiOff, Maximize2, Minimize2 } from 'lucide-react';
import { OfflineModal } from './components/OfflineModal';
import CategoryCatalogue from './components/CategoryCatalogue';
import PuzzleCatalogue from './components/PuzzleCatalogue';
import VerseUnlock from './components/VerseUnlock';
import PuzzleBoard from './components/PuzzleBoard';
import VictoryScreen from './components/VictoryScreen';
import StoryModal from './components/StoryModal';
import ChallengeModal from './components/ChallengeModal';
import ReleaseNotesModal from './components/ReleaseNotesModal';
import { getOverrideSponsorshipStatus, applyUserOverrides } from './utils/authOverride';
import { AppLauncherModal } from './components/AppLauncherModal';
import { FaithDuelManageModal } from './components/FaithDuelManageModal';

const App: React.FC = () => {
  const [appState, setAppState] = useState<AppState>('INTRO');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isOnline, setIsOnline] = useState(() => typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isOfflineAlertOpen, setIsOfflineAlertOpen] = useState(false);
  const [offlinePendingFeature, setOfflinePendingFeature] = useState('');

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.warn("Fullscreen request failed (sandbox restrictions may apply):", err);
      }
      setIsFullscreen(true);
    } else {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      } catch (err) {
        console.warn("Fullscreen exit failed:", err);
      }
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const triggerOnlineAction = (featureName: string, action: () => void) => {
    if (isOnline) {
      action();
    } else {
      setOfflinePendingFeature(featureName);
      setIsOfflineAlertOpen(true);
    }
  };
  const [user, setUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem('bp_quiz_user');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error("Failed to load user", e);
      return null;
    }
  });
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedPuzzle, setSelectedPuzzle] = useState<Puzzle | null>(null);
  const [activeChallenge, setActiveChallenge] = useState<Challenge | null>(null);
  const [isDeepLinkBannerClosed, setIsDeepLinkBannerClosed] = useState(false);
  const [isChallengeCreatorOpen, setIsChallengeCreatorOpen] = useState(false);
  const [isAuthRestricted, setIsAuthRestricted] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBioOpen, setIsBioOpen] = useState(false);
  const [isStoryOpen, setIsStoryOpen] = useState(false);
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isLauncherOpen, setIsLauncherOpen] = useState(false);
  const [launcherPayload, setLauncherPayload] = useState('');
  const [isFaithDuelManageOpen, setIsFaithDuelManageOpen] = useState(false);
  const [faithDuelTargetPuzzle, setFaithDuelTargetPuzzle] = useState<Puzzle | null>(null);
  const hasPromptedInitialLaunch = React.useRef(false);

  useEffect(() => {
    if (activeChallenge) {
      setIsDeepLinkBannerClosed(false);
    }
  }, [activeChallenge]);

  const getActiveChallengeUrl = () => {
    if (!activeChallenge) return "";
    try {
      const payload = btoa(unescape(encodeURIComponent(JSON.stringify(activeChallenge))));
      return `web+bpq://?duel=${payload}`;
    } catch {
      return "";
    }
  };

  const handleListenToStory = () => {
    if (!selectedPuzzle || !selectedPuzzle.storyPassage) return;
    
    // Stop any current speaking
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(selectedPuzzle.storyPassage);
    utterance.rate = 0.9; // Slightly slower for clarity
    window.speechSynthesis.speak(utterance);
  };

  // Browser History Integration
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;
      if (state && state.appState) {
        setAppState(state.appState);
        if (state.categoryId) {
          const cat = CATEGORIES.find(c => c.id === state.categoryId);
          if (cat) setSelectedCategory(cat);
        } else {
          setSelectedCategory(null);
        }
        if (state.puzzleId) {
          const p = CATEGORIES.flatMap(c => c.puzzles).find(p => p.id === state.puzzleId);
          if (p) setSelectedPuzzle(p);
        } else {
          setSelectedPuzzle(null);
        }
        setActiveChallenge(state.challenge || null);
      } else {
        setAppState('INTRO');
        setSelectedCategory(null);
        setSelectedPuzzle(null);
        setActiveChallenge(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Initial state push
    if (!window.history.state) {
      window.history.replaceState({ appState: 'INTRO' }, '');
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Start-up URL Check for Faith Duels
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const duelPayload = urlParams.get('duel');
      if (duelPayload) {
        // Check if running in installed app or standalone mode
        const isInstalledApp = window.matchMedia('(display-mode: standalone)').matches || 
                               (navigator as any).standalone || 
                               document.referrer.includes('android-app://') ||
                               (window.name === 'twa-window' || (window.navigator as any).userAgent?.includes('TWA') || (window.navigator as any).userAgent?.includes('wv'));
        
        let decodedStr = '';
        try {
          decodedStr = decodeURIComponent(escape(atob(duelPayload)));
        } catch {
          decodedStr = atob(duelPayload);
        }
        
        const decodedChallenge = JSON.parse(decodedStr) as Challenge;
        
        if (decodedChallenge && decodedChallenge.puzzleId) {
          const allPuzzles = CATEGORIES.flatMap(c => c.puzzles);
          const matchedPuzzle = allPuzzles.find(p => p.id === decodedChallenge.puzzleId);
          if (matchedPuzzle) {
            if (isInstalledApp) {
              // Directly auto-load the challenge and redirect on-device without paste or prompts
              setActiveChallenge(decodedChallenge);
              setSelectedPuzzle(matchedPuzzle);
              const matchedCategory = CATEGORIES.find(c => c.puzzles.some(p => p.id === matchedPuzzle.id));
              if (matchedCategory) {
                setSelectedCategory(matchedCategory);
              }
              setAppState('VERSE_UNLOCK');
            } else {
              // Standard web browser - prompt user with App Launcher choice modal
              setLauncherPayload(duelPayload);
              setIsLauncherOpen(true);
              
              // Only load the challenger info for the modal's display of the sender's name
              setActiveChallenge(decodedChallenge);
            }
          }
        }
      }
    } catch (e) {
      console.error("Failed to parse start-up duel parameter", e);
    }
  }, []);

  const navigateTo = (newState: AppState, category: Category | null = selectedCategory, puzzle: Puzzle | null = selectedPuzzle, challenge: Challenge | null = activeChallenge) => {
    // If we're moving to a puzzle that doesn't match the current challenge, or if we're explicitly clearing it, terminate the duel state
    const nextChallenge = (challenge && puzzle && challenge.puzzleId !== puzzle.id) ? null : challenge;
    
    setAppState(newState);
    setSelectedCategory(category);
    setSelectedPuzzle(puzzle);
    setActiveChallenge(nextChallenge);
    
    // Manage URL: Remove duel parameter if challenge is no longer active
    try {
      const url = new URL(window.location.href);
      if (!nextChallenge) {
        url.searchParams.delete('duel');
      }
      
      const newPath = url.pathname + url.search;
      window.history.pushState({ 
        appState: newState, 
        categoryId: category?.id,
        puzzleId: puzzle?.id,
        challenge: nextChallenge
      }, '', newPath);
    } catch (e) {
      console.warn("History push failed", e);
      // Fallback to basic push
      window.history.pushState({ appState: newState }, '');
    }
  };

  const [lastStats, setLastStats] = useState<{ 
    moves: number; 
    time: number; 
    faithScore: number;
    formattedDate?: string;
    excelFullTSV?: string;
  } | null>(null);

  const [settings, setSettings] = useState<AppSettings>(() => {
    try {
      const saved = localStorage.getItem('bp_quiz_settings');
      return saved ? JSON.parse(saved) : {
        musicEnabled: true,
        sfxEnabled: true,
        hapticsEnabled: true,
        theme: 'light',
        textSize: 16
      };
    } catch (e) {
      console.error("Failed to load settings", e);
      return { musicEnabled: true, sfxEnabled: true, hapticsEnabled: true, theme: 'light', textSize: 16 };
    }
  });
  
  const [progress, setProgress] = useState<UserProgress>(() => {
    try {
      const saved = localStorage.getItem('bp_quiz_progress');
      const defaultProgress: UserProgress = { 
        solvedPuzzles: [], 
        inProgress: {}, 
        isSponsored: false, 
        wonChallenges: 0,
        totalDuelTime: 0,
        totalDuelMoves: 0,
        totalDuelCount: 0,
        totalFaithScore: 0,
        lifetimeDuelCount: 0
      };
      if (!saved) return defaultProgress;
      const parsed = JSON.parse(saved);
      return { ...defaultProgress, ...parsed };
    } catch (e) {
      console.error("Failed to load progress", e);
      return { 
        solvedPuzzles: [], 
        inProgress: {}, 
        isSponsored: false, 
        wonChallenges: 0,
        totalDuelTime: 0,
        totalDuelMoves: 0,
        totalDuelCount: 0,
        totalFaithScore: 0,
        lifetimeDuelCount: 0
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('bp_quiz_progress', JSON.stringify(progress));
  }, [progress]);

  useEffect(() => {
    localStorage.setItem('bp_quiz_settings', JSON.stringify(settings));
    // Apply theme to body
    document.body.className = `theme-${settings.theme}`;
  }, [settings]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('bp_quiz_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('bp_quiz_user');
    }
  }, [user]);

  // Incoming deep links are registered and parsed globally on boot.
  // Manual accepts can be processed via FaithDuelManageModal inputs.

  const handleLogin = (newUser: User) => {
    const overriddenUser = applyUserOverrides(newUser);
    setUser(overriddenUser);
    
    // Check for sponsorship override based on username
    if (overriddenUser.username) {
      const isSponsored = getOverrideSponsorshipStatus(overriddenUser.username, progress.isSponsored);
      if (isSponsored !== progress.isSponsored) {
        setProgress(prev => ({ ...prev, isSponsored }));
      }
    }

    navigateTo(activeChallenge ? 'VERSE_UNLOCK' : 'CATEGORIES');
  };

  const handleChallengeRequest = (p: Puzzle) => {
    setFaithDuelTargetPuzzle(p);
    setIsFaithDuelManageOpen(true);
  };

  const handlePuzzleComplete = (finalMoves: number, finalTime: number) => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setIsFullscreen(false);

    if (selectedPuzzle) {
      const isDuel = !!activeChallenge;
      
      // Duel Score Formula Calculation (Strictly for Duels)
      let puzzleFaithScore = 0;
      if (isDuel) {
        const T = Math.max(0, 360 - finalTime);
        const M = Math.max(0, 180 - finalMoves);
        puzzleFaithScore = (T + M + 10) / 10;
      }

      // Generate date and code immediately for local persistence prior to rendering
      const finishDate = new Date();
      const time = finishDate.toTimeString().split(' ')[0];
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const dayName = weekdays[finishDate.getDay()];
      const dayNum = finishDate.getDate();
      const monthName = months[finishDate.getMonth()];
      const yearNum = finishDate.getFullYear();
      const formattedDate = `${time}, ${dayName}, ${dayNum} ${monthName} ${yearNum}`;

      const activeUserId = user?.email || user?.id || user?.username || "Guest_Player";
      const roundedFaithScore = Math.round(puzzleFaithScore * 10) / 10;
      const excelHeaderTSV = "Player ID\tCompletion Timestamp\tPuzzle Title\tMoves\tPlay Duration (Seconds)\tFaith Score";
      const excelRowTSV = `${activeUserId}\t${formattedDate}\t${selectedPuzzle.title}\t${finalMoves}\t${finalTime}\t${roundedFaithScore}`;
      const excelFullTSV = `${excelHeaderTSV}\n${excelRowTSV}`;

      // Save directly to raw score archive as a fail-safe on completion, before navigate to VICTORY
      try {
        const stored = localStorage.getItem('bp_quiz_score_archive');
        let archive: Array<{ puzzleTitle: string; formattedDate: string; code: string; timestamp: number }> = [];
        if (stored) {
          archive = JSON.parse(stored);
        }
        
        const isDuplicate = archive.some(
          item => item.puzzleTitle === selectedPuzzle.title && item.formattedDate === formattedDate
        );
        
        if (!isDuplicate) {
          const newRecord = {
            puzzleTitle: selectedPuzzle.title,
            formattedDate,
            code: excelFullTSV,
            timestamp: Date.now()
          };
          archive = [newRecord, ...archive];
          archive = archive.slice(0, 3);
          localStorage.setItem('bp_quiz_score_archive', JSON.stringify(archive));
        }
      } catch (e) {
        console.error("Failed to update score archive in handlePuzzleComplete", e);
      }

      // Calculate next state values before update for session sync
      const isNewSuccess = !progress.solvedPuzzles.includes(selectedPuzzle.id);
      const nextSolvedCount = isNewSuccess ? progress.solvedPuzzles.length + 1 : progress.solvedPuzzles.length;
      const nextWonChallenges = isDuel ? progress.wonChallenges + 1 : progress.wonChallenges;

      setProgress(prev => {
        const nextSolved = isNewSuccess ? [...prev.solvedPuzzles, selectedPuzzle.id] : prev.solvedPuzzles;
        
        if (isDuel) {
          return {
            ...prev,
            solvedPuzzles: nextSolved,
            totalDuelTime: prev.totalDuelTime + finalTime,
            totalDuelMoves: prev.totalDuelMoves + finalMoves,
            totalDuelCount: prev.totalDuelCount + 1,
            lifetimeDuelCount: prev.lifetimeDuelCount + 1,
            wonChallenges: prev.wonChallenges + 1,
            totalFaithScore: prev.totalFaithScore + puzzleFaithScore
          };
        }

        return {
          ...prev,
          solvedPuzzles: nextSolved
        };
      });

      setLastStats({ 
        moves: finalMoves, 
        time: finalTime, 
        faithScore: puzzleFaithScore,
        formattedDate,
        excelFullTSV
      });

      // Update User Stats (Session-based UI sync)
      if (user) {
        setUser(prev => {
          if (!prev) return null;
          // Note: totalScore is a legacy weighted metric, Faith Score in bio modal 
          // uses the direct progress.totalFaithScore which is more accurate.
          const newTotalScore = (nextSolvedCount * 100) + (nextWonChallenges * 500);
          return {
            ...prev,
            stats: { 
              scrolls: nextSolvedCount, 
              totalScore: newTotalScore 
            }
          };
        });
      }
    }
    navigateTo('VICTORY');
  };

  useEffect(() => {
    if (user?.username) {
      const isSponsored = getOverrideSponsorshipStatus(user.username, progress.isSponsored);
      if (isSponsored !== progress.isSponsored) {
        setProgress(prev => ({ ...prev, isSponsored }));
      }
    }
  }, [user]);

  const renderContent = () => {
    switch (appState) {
      case 'INTRO': return <IntroVideo onEnter={() => navigateTo(user ? 'CATEGORIES' : 'LOGIN')} />;
      case 'LOGIN': return <Login onLogin={handleLogin} />;
      case 'CATEGORIES': return (
        <div className="flex-1 flex flex-col relative">
          <CategoryCatalogue 
            categories={CATEGORIES} 
            onSelect={(cat) => navigateTo('PUZZLE_LIST', cat)} 
            user={user} 
            progress={progress} 
            onToggleSponsorship={(s) => setProgress(p => ({ ...p, isSponsored: s }))} 
            onAcceptDuel={() => {
              setFaithDuelTargetPuzzle(null);
              setIsFaithDuelManageOpen(true);
            }}
            triggerOnlineAction={triggerOnlineAction}
          />
        </div>
      );
      case 'PUZZLE_LIST': return selectedCategory ? (
        <div className="flex-1 flex flex-col relative">
          <PuzzleCatalogue 
            category={selectedCategory} 
            onSelect={(p) => navigateTo(progress.solvedPuzzles.includes(p.id) ? 'PLAYING' : 'VERSE_UNLOCK', selectedCategory, p)} 
            onReadStory={(p) => { setSelectedPuzzle(p); setIsStoryOpen(true); }}
            onChallenge={handleChallengeRequest} 
            onBack={() => navigateTo('CATEGORIES', null)} 
            onOpenSupport={() => setIsSupportModalOpen(true)}
            progress={progress} 
            user={user}
            onToggleSponsorship={(s) => setProgress(p => ({ ...p, isSponsored: s }))} 
            triggerOnlineAction={triggerOnlineAction}
          />
        </div>
      ) : null;
      case 'VERSE_UNLOCK': return selectedPuzzle ? <VerseUnlock puzzle={selectedPuzzle} challenge={activeChallenge} settings={settings} onSuccess={() => navigateTo('PLAYING')} onBack={() => navigateTo(selectedCategory ? 'PUZZLE_LIST' : 'CATEGORIES', selectedCategory, null, null)} /> : null;
      case 'PLAYING': return selectedPuzzle ? <PuzzleBoard puzzle={selectedPuzzle} isChallenge={!!activeChallenge} settings={settings} onComplete={handlePuzzleComplete} onBack={() => navigateTo(selectedCategory ? 'PUZZLE_LIST' : 'CATEGORIES', selectedCategory, null)} isFullscreen={isFullscreen} /> : null;
      case 'VICTORY': return selectedPuzzle ? (
        <VictoryScreen 
          puzzle={selectedPuzzle} 
          onNext={() => navigateTo('CATEGORIES', null, null, null)} 
          isChallenge={!!activeChallenge} 
          challengerName={activeChallenge?.challengerName} 
          onRevenge={() => handleChallengeRequest(selectedPuzzle)} 
          stats={lastStats || undefined} 
          onOpenStory={() => setIsStoryOpen(true)}
          onSupport={() => setIsSupportModalOpen(true)}
          user={user}
          triggerOnlineAction={triggerOnlineAction}
        />
      ) : null;
      default: return null;
    }
  };

  const handleLogout = () => {
    setUser(null);
    navigateTo('LOGIN', null, null, null);
    setIsSettingsOpen(false);
  };

  const handleDeleteAccount = () => {
    if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      setUser(null);
      setProgress({ 
        solvedPuzzles: [], 
        inProgress: {}, 
        isSponsored: false, 
        wonChallenges: 0,
        totalDuelTime: 0,
        totalDuelMoves: 0,
        totalDuelCount: 0,
        totalFaithScore: 0,
        lifetimeDuelCount: 0
      });
      navigateTo('LOGIN', null, null, null);
      setIsSettingsOpen(false);
    }
  };

  const handleResetProgress = () => {
    if (confirm('Are you sure you want to reset your progress? Your statistics and solved puzzles will be cleared, but your lifetime Faith Score records will remain.')) {
      setProgress(prev => ({ 
        ...prev, 
        solvedPuzzles: [], 
        inProgress: {},
        wonChallenges: 0,
        totalDuelTime: 0,
        totalDuelMoves: 0,
        totalDuelCount: 0
      }));
      if (user) {
        setUser({ ...user, stats: { scrolls: 0, totalScore: 0 } });
      }
      setIsSettingsOpen(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${settings.theme === 'dark' ? 'theme-dark' : settings.theme === 'sepia' ? 'theme-sepia' : 'theme-light'}`}>
      {/* Offline Status Banner */}
      {!isOnline && ['CATEGORIES', 'PUZZLE_LIST', 'VERSE_UNLOCK', 'PLAYING', 'VICTORY'].includes(appState) && (
        <div className="bg-amber-600 dark:bg-amber-700 text-white text-xs md:text-sm px-4 py-2.5 flex items-center justify-center gap-2 font-sans font-bold shadow-md z-[150] shrink-0 animate-in slide-in-from-top duration-300 border-b border-amber-500/20">
          <WifiOff className="w-4 h-4 text-white animate-pulse shrink-0" />
          <span>Offline Mode: Local Bible gameplay is fully active, but online submissions, duels, and sharing are temporarily paused.</span>
        </div>
      )}

      {renderContent()}
      
      {/* Floating Buttons Control Array (Fullscreen and Settings) */}
      {['CATEGORIES', 'PUZZLE_LIST', 'VERSE_UNLOCK', 'PLAYING', 'VICTORY'].includes(appState) && (
        <div className="fixed bottom-6 right-6 md:bottom-8 md:right-8 flex gap-3 z-[200] mb-safe mr-safe">
          {/* Floating Full-Screen Button beside Settings */}
          {appState === 'PLAYING' && (
            <button 
              onClick={toggleFullscreen}
              className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-white rounded-2xl shadow-2xl border border-gray-100 text-gray-400 hover:text-blue-600 transition-all active:scale-95"
              aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="w-6 h-6 md:w-7 md:h-7 text-blue-600 animate-pulse" />
              ) : (
                <Maximize2 className="w-6 h-6 md:w-7 md:h-7" />
              )}
            </button>
          )}

          {/* Settings Button */}
          {!isFullscreen && (
            <button 
              onClick={() => setIsSettingsOpen(true)}
              className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-white rounded-2xl shadow-2xl border border-gray-100 text-gray-400 hover:text-blue-600 transition-all active:scale-95"
              aria-label="Settings"
            >
              <Settings className="w-6 h-6 md:w-7 md:h-7" />
            </button>
          )}
        </div>
      )}

      {isChallengeCreatorOpen && selectedPuzzle && (
        <ChallengeModal 
          puzzle={selectedPuzzle} 
          user={user} 
          onClose={() => setIsChallengeCreatorOpen(false)} 
        />
      )}
      <AuthRestrictionModal 
        isOpen={isAuthRestricted} 
        onClose={() => setIsAuthRestricted(false)} 
        onGoToLogin={() => { setIsAuthRestricted(false); navigateTo('LOGIN'); }} 
      />
      <AppLauncherModal 
        isOpen={isLauncherOpen}
        onClose={() => setIsLauncherOpen(false)}
        payload={launcherPayload}
        challengerName={activeChallenge?.challengerName}
        triggerOnlineAction={triggerOnlineAction}
      />
      <SettingsModal 
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        user={user}
        settings={settings}
        onUpdateSettings={(s) => setSettings(prev => ({ ...prev, ...s }))}
        onLogout={handleLogout}
        onDeleteAccount={handleDeleteAccount}
        onResetProgress={handleResetProgress}
        onOpenProfile={() => { setIsSettingsOpen(false); setIsBioOpen(true); }}
        onOpenReleaseNotes={() => { setIsSettingsOpen(false); setIsReleaseNotesOpen(true); }}
        onOpenSupport={() => setIsSupportModalOpen(true)}
        triggerOnlineAction={triggerOnlineAction}
      />
      <ReleaseNotesModal 
        isOpen={isReleaseNotesOpen}
        onClose={() => setIsReleaseNotesOpen(false)}
      />
      <SupportModal 
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        onUnLockAll={() => setProgress(p => ({ ...p, isSponsored: true }))}
        isAlreadyUnlocked={progress.isSponsored}
      />

      <OfflineModal 
        isOpen={isOfflineAlertOpen}
        onClose={() => setIsOfflineAlertOpen(false)}
        featureName={offlinePendingFeature}
      />
      <FaithDuelManageModal
        isOpen={isFaithDuelManageOpen}
        onClose={() => setIsFaithDuelManageOpen(false)}
        user={user}
        progress={progress}
        puzzle={faithDuelTargetPuzzle}
        onOpenSupport={() => setIsSupportModalOpen(true)}
        onGoToLogin={() => navigateTo('LOGIN')}
        onChallengeAccepted={(challenge, targetPuzzle) => {
          setActiveChallenge(challenge);
          setSelectedPuzzle(targetPuzzle);
          navigateTo('VERSE_UNLOCK', selectedCategory, targetPuzzle, challenge);
        }}
        onCreateChallenge={(p) => {
          setSelectedPuzzle(p);
          setIsChallengeCreatorOpen(true);
        }}
        allPuzzles={CATEGORIES.flatMap(c => c.puzzles)}
      />
      <BioModal 
        isOpen={isBioOpen}
        onClose={() => setIsBioOpen(false)}
        user={user}
        progress={progress}
        isCurrentUser={true}
        onUpdateAvatar={(avatar) => setUser(prev => prev ? ({ ...prev, avatar }) : null)}
      />
      {selectedPuzzle && (
        <StoryModal
          puzzle={selectedPuzzle}
          isOpen={isStoryOpen}
          onClose={() => {
            setIsStoryOpen(false);
            window.speechSynthesis.cancel();
          }}
          settings={settings}
          onUpdateSettings={(s) => setSettings(prev => ({ ...prev, ...s }))}
          onListen={handleListenToStory}
        />
      )}
    </div>
  );
};

export default App;